import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ArrowRight } from "lucide-react";
import { DataTable } from "@/components/data-table/data-table";
import { getColumns } from "./Columns";
import { zoomWorkspaceSearch } from "./workspaceSearchZoom";
import { useSearchScrollRestoration } from "@/pages/workspace/hooks/useSearchScrollRestoration";
import useWorkspaceTableData from "@/pages/workspace/hooks/useWorkspaceTableData";
import { WorkspaceTableRowSkeleton } from "./WorkspaceTableRowSkeleton";
import CreateWorkspaceDialog from "@/pages/workspace/components/CreateWorkspaceDialog";
import { useContent } from "@/hooks/useContent";
import useSettingsStore from "@/stores/settings.store";
import useUsers from "@/hooks/useUsers";
import useGroups from "@/hooks/useGroups";
import { Subscription } from "@/models/subscriptions/Subscription";
import SharedAccessDialog from "./SharedAccessDialog";
import SubTenantsDialog from "./SubTenantsDialog";
import useUserTokenStore from "@/stores/user-token.store";
import { Permission } from "@/lib/permissions/Permission.enum";
import { permissionMap } from "@/lib/permissions/permissionMap";
import { cn } from "@/lib/utils";
import {
  CONTENT_SEARCH_MIN_LENGTH,
  useWorkspaceContentSearch,
} from "@/hooks/useWorkspaceContentSearch";
import {
  ResultTypeSwitch,
  type WorkspaceSearchResultType,
} from "@/pages/workspace/components/content-search/ResultTypeSwitch";
import WorkspaceContentSearchResults, {
  OVERVIEW_DOCUMENTS_PER_WORKSPACE,
  OVERVIEW_WORKSPACE_PAGE_SIZE,
} from "@/pages/workspace/components/content-search/WorkspaceContentSearchResults";
import { workspaceCountLabel } from "@/pages/workspace/components/content-search/contentSearchLabels";
import { getWorkspaceSearchLayout } from "@/pages/workspace/components/content-search/workspaceSearchLayout";
import { useLocation } from "react-router-dom";
import { readWorkspaceSearchParams } from "@/lib/navigation/workspaceSearchParams";
import { useWorkspaceSearchAddress } from "@/pages/workspace/hooks/useWorkspaceSearchAddress";

/** In the combined view the table is a preview: this many name matches, then a link to the rest. */
export const ALL_MODE_WORKSPACE_PREVIEW_ROWS = 5;

const WorkspaceTable = () => {
  const {
    data,
    search,
    debouncedSearch,
    totalRecords,
    pageCount,
    pageIndex,
    pageSize,
    sorting,
    setPageIndex,
    setPageSize,
    setSorting,
    handleSearchChange,
    handleRowClick,
    users,
    isLoading,
    parentTenant,
    availableSubtenants,
    currentTenant,
  } = useWorkspaceTableData();
  const { t } = useContent();
  const { isDarkMode } = useSettingsStore();
  const { useQuerySubscriptions } = useUsers();
  const { useMyGroupsSubscriptionEntityPaths } = useGroups();
  const [subscriptions, setSubscriptions] = useState<Subscription[]>([]);
  const fetchedSubscriptions = useQuerySubscriptions();
  const { groupSubscriptionEntityPaths } = useMyGroupsSubscriptionEntityPaths();
  const hasCreateFeatureGroups = useUserTokenStore((s) =>
    s.permissions.includes(permissionMap[Permission.CreateFeatureGroups])
  );

  // Dialog state
  const [sharedAccessDialogOpen, setSharedAccessDialogOpen] = useState(false);
  const [sharedAccessUserIds, setSharedAccessUserIds] = useState<string[]>([]);
  const [subTenantsDialogOpen, setSubTenantsDialogOpen] = useState(false);
  const [subTenantsNames, setSubTenantsNames] = useState<string[]>([]);

  // Content search (LAR-235): while a search is active the page also lists the documents
  // whose title or text matches, and the reader can zoom on workspaces, documents or one
  // workspace. The state resets when the search is cleared.
  const searchTerm = debouncedSearch.trim();
  const isSearching = searchTerm.length >= CONTENT_SEARCH_MIN_LENGTH;
  const location = useLocation();
  const [resultType, setResultType] = useState<WorkspaceSearchResultType>(
    () => readWorkspaceSearchParams(location.search).resultType
  );
  const [zoomedWorkspaceId, setZoomedWorkspaceId] = useState<string | null>(
    () => readWorkspaceSearchParams(location.search).zoomedWorkspaceId
  );
  const [zoomedFrom, setZoomedFrom] = useState<WorkspaceSearchResultType>(
    () => readWorkspaceSearchParams(location.search).zoomedFrom ?? "documents"
  );
  useWorkspaceSearchAddress({
    search: searchTerm,
    resultType,
    zoomedWorkspaceId,
    zoomedFrom,
  });
  // Each view (term, result type, zoom) comes back scrolled where the reader left it.
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  useSearchScrollRestoration(
    scrollContainerRef,
    isSearching
      ? JSON.stringify([searchTerm, resultType, zoomedWorkspaceId])
      : null
  );
  // Same query as the overview list below, so react-query serves both from one request.
  const contentTotals = useWorkspaceContentSearch({
    searchTerm,
    workspacePageSize: OVERVIEW_WORKSPACE_PAGE_SIZE,
    documentsPerWorkspace: OVERVIEW_DOCUMENTS_PER_WORKSPACE,
    enabled: isSearching,
  });

  useEffect(() => {
    if (isSearching) return;
    setResultType("all");
    setZoomedWorkspaceId(null);
    setZoomedFrom("documents");
  }, [isSearching]);

  const handleZoomWorkspace = useCallback(
    (workspaceId: string | null) => {
      const next = zoomWorkspaceSearch(
        { resultType, zoomedWorkspaceId, zoomedFrom },
        workspaceId
      );
      setResultType(next.resultType);
      setZoomedWorkspaceId(next.zoomedWorkspaceId);
      setZoomedFrom(next.zoomedFrom);
    },
    [resultType, zoomedWorkspaceId, zoomedFrom]
  );

  const {
    showWorkspaces,
    previewWorkspaces,
    showWorkspaceRows,
    showDocuments,
  } = getWorkspaceSearchLayout({
    isSearching,
    resultType,
    workspaceCount: totalRecords,
    isLoadingWorkspaces: isLoading,
  });
  const hasMoreWorkspacesThanPreview =
    totalRecords !== undefined &&
    totalRecords > ALL_MODE_WORKSPACE_PREVIEW_ROWS;

  const tableData = useMemo(
    () =>
      previewWorkspaces ? data.slice(0, ALL_MODE_WORKSPACE_PREVIEW_ROWS) : data,
    [data, previewWorkspaces]
  );

  useEffect(() => {
    if (fetchedSubscriptions.isFetched && fetchedSubscriptions.data) {
      setSubscriptions(fetchedSubscriptions.data);
    }
  }, [fetchedSubscriptions.isFetched, fetchedSubscriptions.data]);

  const handleOpenSharedAccessDialog = useCallback((userIds: string[]) => {
    setSharedAccessUserIds(userIds);
    setSharedAccessDialogOpen(true);
  }, []);

  const handleOpenSubTenantsDialog = useCallback((names: string[]) => {
    setSubTenantsNames(names);
    setSubTenantsDialogOpen(true);
  }, []);

  // Logo validation - calculate once instead of for each row
  const darkLogo = t("sidebar.collapsed.logo.dark");
  const lightLogo = t("sidebar.collapsed.logo.light");
  const hasValidDarkLogo =
    Boolean(darkLogo) &&
    darkLogo !== "sidebar.collapsed.logo.dark" &&
    darkLogo.trim() !== "";
  const hasValidLightLogo =
    Boolean(lightLogo) &&
    lightLogo !== "sidebar.collapsed.logo.light" &&
    lightLogo.trim() !== "";

  const workspaceHeading = isSearching && showWorkspaces && (
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-2.5 gap-y-1">
      <h2 className="text-sm font-semibold">
        Workspaces med match på “{searchTerm}”
      </h2>
      {totalRecords !== undefined && (
        <span className="text-xs text-muted-foreground">
          {workspaceCountLabel(totalRecords)}
          {previewWorkspaces &&
            hasMoreWorkspacesThanPreview &&
            ` · viser ${ALL_MODE_WORKSPACE_PREVIEW_ROWS}`}
        </span>
      )}
    </div>
  );

  const previewFooter =
    previewWorkspaces && hasMoreWorkspacesThanPreview ? (
      <button
        type="button"
        className="inline-flex h-8 items-center gap-1 self-start text-sm font-medium text-primary hover:underline"
        onClick={() => setResultType("workspaces")}
      >
        Vis alle {totalRecords} workspaces
        <ArrowRight className="size-3.5" aria-hidden="true" />
      </button>
    ) : undefined;

  return (
    <>
      {/* While searching this is the scroll container, and a scroll container clips on both
          axes: the 3px focus rings drawn outside the toolbar's controls would be cut where
          the search field meets its top-left edge. The 4px padding gives them room; the
          matching negative margins and the taller box keep the content where it was. */}
      <div
        ref={scrollContainerRef}
        className={cn(
          "flex h-full flex-col",
          isSearching &&
            "-mx-1 -mt-1 h-[calc(100%+0.25rem)] overflow-y-auto px-1 pt-1"
        )}
      >
        <DataTable
          columns={useMemo(
            () =>
              getColumns(
                users,
                isDarkMode,
                parentTenant,
                subscriptions || [],
                availableSubtenants,
                currentTenant,
                handleOpenSharedAccessDialog,
                handleOpenSubTenantsDialog,
                hasValidDarkLogo,
                hasValidLightLogo,
                darkLogo,
                lightLogo,
                groupSubscriptionEntityPaths,
                hasCreateFeatureGroups,
                searchTerm
              ),
            [
              users,
              parentTenant,
              isDarkMode,
              subscriptions,
              availableSubtenants,
              currentTenant,
              handleOpenSharedAccessDialog,
              handleOpenSubTenantsDialog,
              hasValidDarkLogo,
              hasValidLightLogo,
              darkLogo,
              lightLogo,
              groupSubscriptionEntityPaths,
              hasCreateFeatureGroups,
              searchTerm,
            ]
          )}
          data={tableData}
          pageCount={pageCount}
          pageIndex={pageIndex}
          pageSize={pageSize}
          setPageIndex={setPageIndex}
          setPageSize={setPageSize}
          sorting={sorting}
          setSorting={setSorting}
          handleSearchChange={handleSearchChange}
          search={search}
          handleRowClick={handleRowClick}
          isLoading={isLoading || fetchedSubscriptions.isLoading}
          skeletonComponent={WorkspaceTableRowSkeleton}
          skeletonRows={
            previewWorkspaces ? ALL_MODE_WORKSPACE_PREVIEW_ROWS : undefined
          }
          stickyHeader={true}
          createNode={
            <CreateWorkspaceDialog
              availableSubtenants={availableSubtenants || []}
              isLoading={isLoading}
            />
          }
          shouldDisplayHoverContent={true}
          searchActions={
            isSearching ? (
              <ResultTypeSwitch
                value={resultType}
                onChange={(next) => {
                  setResultType(next);
                  if (next !== "documents") setZoomedWorkspaceId(null);
                }}
                workspaceCount={totalRecords}
                documentCount={
                  contentTotals.isPending || contentTotals.isError
                    ? undefined
                    : contentTotals.totalDocuments
                }
              />
            ) : undefined
          }
          beforeTable={workspaceHeading || undefined}
          footer={previewFooter}
          hidePagination={previewWorkspaces && !hasMoreWorkspacesThanPreview}
          hideRows={!showWorkspaceRows}
          className={isSearching ? "h-auto shrink-0" : undefined}
        />
        {showDocuments && (
          <WorkspaceContentSearchResults
            className={cn("pb-6", showWorkspaces && "mt-2")}
            searchTerm={searchTerm}
            zoomedWorkspaceId={zoomedWorkspaceId}
            onZoomWorkspace={handleZoomWorkspace}
          />
        )}
      </div>
      <SharedAccessDialog
        open={sharedAccessDialogOpen}
        onOpenChange={setSharedAccessDialogOpen}
        sharedUserIds={sharedAccessUserIds}
        tenantUsers={users}
      />
      <SubTenantsDialog
        open={subTenantsDialogOpen}
        onOpenChange={setSubTenantsDialogOpen}
        tenantNames={subTenantsNames}
      />
    </>
  );
};

export default WorkspaceTable;
